import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import PropTypes from 'prop-types';
import TableItem from './TableItem';
import AppTableContainer from '@projectK/components/AppTableContainer';
import AppScrollbar from '@projectK/components/AppScrollbar';
import { TableCell, TableSortLabel, TableRow } from '@mui/material';
import { Fonts } from '@projectK/constants';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import AppTableLoader from '@projectK/components/AppTableLoader';
import AppTableNoData from '@projectK/components/AppTableNoData';

const getStyles = ({ companyTheme, tournamentId }) => {
  return tournamentId
    ? {
        borderBottom: '0 none',
        '& .tableCell': {
          fontSize: 13,
          padding: 2,
          fontWeight: Fonts.BOLD,
          backgroundColor: '#ffffff',
          color: '#000',
          '&:first-of-type': {
            pl: 5,
          },
          '&:last-of-type': {
            pr: 5,
          },
          '&:hover': {
            color: '#000 !important', // Force black text on hover
          },
        },
      }
    : {
        borderBottom: '0 none',
        '& .tableCell': {
          fontSize: 13,
          padding: 2,
          fontWeight: Fonts.BOLD,
          backgroundColor: companyTheme ? companyTheme.primary.main : '#211251',
          color: '#ffffff',
          '&:first-of-type': {
            pl: 5,
          },
          '&:last-of-type': {
            pr: 5,
          },
        },
      };
};
const PromoCodeTable = ({
  promocodeData,
  loading,
  onDelete,
  fetchData,
  nameSorting,
  setNameSorting,
  promoCodeSorting,
  setPromoCodeSorting,
  productNameSorting,
  setProductNameSorting,
  startDateSorting,
  setStartDateSorting,
  endDateSorting,
  setEndDateSorting,
  typeSorting,
  setTypeSorting,
  valueSorting,
  setValueSorting,
  statusSorting,
  setStatusSorting,
  serviceTypeSorting,
  setServiceTypeSorting,
  tournamentId,
  exportLoading,
}) => {
  const companyTheme = CompanyTheme();
  const { verbiageSettings } = useApplicationSettings();

  const updatedData = promocodeData.map((promocode) => ({
    ...promocode,
    discount_value: parseFloat(promocode.discount_value),
  }));

  const handleSort = (column) => {
    setNameSorting(column === 'promo_code_name' ? !nameSorting : null);
    setPromoCodeSorting(column === 'code' ? !promoCodeSorting : null);
    setProductNameSorting(
      column === 'product_name' ? !productNameSorting : null,
    );
    setStartDateSorting(column === 'start_date' ? !startDateSorting : null);
    setEndDateSorting(column === 'end_date' ? !endDateSorting : null);
    setTypeSorting(column === 'discount_type' ? !typeSorting : null);
    setValueSorting(column === 'discount_value' ? !valueSorting : null);
    setStatusSorting(column === 'is_active' ? !statusSorting : null);
    setServiceTypeSorting(
      column === 'service_type' ? !serviceTypeSorting : null,
    );
  };
  const canHideSortIcon = (column) => {
    const columnToState = {
      promo_code_name: nameSorting,
      code: promoCodeSorting,
      product_name: productNameSorting,
      start_date: startDateSorting,
      end_date: endDateSorting,
      discount_type: typeSorting,
      discount_value: valueSorting,
      is_active: statusSorting,
      service_type: serviceTypeSorting,
    };
    for (const columnName in columnToState) {
      if (columnName !== column && columnToState[columnName] !== null) {
        return true;
      }
    }
    return false;
  };
  return (
    <AppTableContainer className="app-table-container">
      <AppTableLoader loading={loading || exportLoading} className={'app-table-loader'}/>
      <AppScrollbar className='max-h'>
        <Table
          stickyHeader
          className={`${tournamentId ? 'purchase_table' : ''}`}
        >
          <TableHead
            className={`${tournamentId ? 'session-table-header' : ''}`}
            sx={getStyles({ companyTheme, tournamentId })}
          >
            <TableRow>
              <TableCell className='tableCell'>
                <TableSortLabel
                  active={nameSorting}
                  direction={nameSorting ? 'asc' : 'desc'}
                  onClick={() => handleSort('promo_code_name')}
                  hideSortIcon={canHideSortIcon('promo_code_name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell className='tableCell'>
                <TableSortLabel
                  active={promoCodeSorting}
                  direction={promoCodeSorting ? 'asc' : 'desc'}
                  onClick={() => handleSort('code')}
                  hideSortIcon={canHideSortIcon('code')}
                >
                  Promo Code
                </TableSortLabel>
              </TableCell>
              {!tournamentId && (
                <>
                  <TableCell className='tableCell'>
                    <TableSortLabel
                      active={serviceTypeSorting}
                      direction={serviceTypeSorting ? 'asc' : 'desc'}
                      onClick={() => handleSort('service_type')}
                      hideSortIcon={canHideSortIcon('service_type')}
                    >
                      Service Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className='tableCell'>
                    <TableSortLabel
                      active={productNameSorting}
                      direction={productNameSorting ? 'asc' : 'desc'}
                      onClick={() => handleSort('product_name')}
                      hideSortIcon={canHideSortIcon('product_name')}
                    >
                      Service Name
                    </TableSortLabel>
                  </TableCell>
                </>
              )}
              <TableCell className='tableCell' align='left'>
                <TableSortLabel
                  active={startDateSorting}
                  direction={startDateSorting ? 'asc' : 'desc'}
                  onClick={() => handleSort('start_date')}
                  hideSortIcon={canHideSortIcon('start_date')}
                >
                  Start Date
                </TableSortLabel>
              </TableCell>
              <TableCell className='tableCell' align='left'>
                <TableSortLabel
                  active={endDateSorting}
                  direction={endDateSorting ? 'asc' : 'desc'}
                  onClick={() => handleSort('end_date')}
                  hideSortIcon={canHideSortIcon('end_date')}
                >
                  End Date
                </TableSortLabel>
              </TableCell>
              <TableCell className='tableCell' align='left'>
                <TableSortLabel
                  active={typeSorting}
                  direction={typeSorting ? 'asc' : 'desc'}
                  onClick={() => handleSort('discount_type')}
                  hideSortIcon={canHideSortIcon('discount_type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell className='tableCell' align='left'>
                <TableSortLabel
                  active={valueSorting}
                  direction={valueSorting ? 'asc' : 'desc'}
                  onClick={() => handleSort('discount_value')}
                  hideSortIcon={canHideSortIcon('discount_value')}
                >
                  Value
                </TableSortLabel>
              </TableCell>
              <TableCell className='tableCell' align='left'>
                <TableSortLabel
                  active={statusSorting}
                  direction={statusSorting ? 'asc' : 'desc'}
                  onClick={() => handleSort('is_active')}
                  hideSortIcon={canHideSortIcon('is_active')}
                >
                  Is Active
                </TableSortLabel>
              </TableCell>
              <TableCell className='tableCell' align='right'>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
           <TableBody>         
            {updatedData.length > 0 ? (
             updatedData.map((data) => (
                <TableItem
                  data={data}
                  key={data.id}
                  tournamentId={tournamentId}
                  onDelete={onDelete}
                  fetchData={fetchData}
                  verbiageSettings={verbiageSettings}
                />
              ))
               ) : (
             <AppTableNoData
               colSpan={10}
               message="No Promo code Found"
               loading={loading}
               />
              )}
          </TableBody>
        </Table>
      </AppScrollbar>
    </AppTableContainer>
  );
};

export default PromoCodeTable;
PromoCodeTable.defaultProps = {
  promocodeData: [],
};

PromoCodeTable.propTypes = {
  promocodeData: PropTypes.array,
  loading: PropTypes.bool,
  onDelete: PropTypes.func,
  fetchData: PropTypes.func,
  nameSorting: PropTypes.bool,
  setNameSorting: PropTypes.func,
  promoCodeSorting: PropTypes.bool,
  setPromoCodeSorting: PropTypes.func,
  productNameSorting: PropTypes.bool,
  setProductNameSorting: PropTypes.func,
  startDateSorting: PropTypes.bool,
  setStartDateSorting: PropTypes.func,
  endDateSorting: PropTypes.bool,
  setEndDateSorting: PropTypes.func,
  typeSorting: PropTypes.bool,
  setTypeSorting: PropTypes.func,
  valueSorting: PropTypes.bool,
  setValueSorting: PropTypes.func,
  statusSorting: PropTypes.bool,
  setStatusSorting: PropTypes.func,
  serviceTypeSorting: PropTypes.bool,
  setServiceTypeSorting: PropTypes.func,
  tournamentId: PropTypes.bool,
  exportLoading: PropTypes.bool,
};
